// ============================================================================
// Logistics-order CONTENTS modal — opened by clicking a row on the Operations
// board. Its whole job is to answer "what items are on this truck": each
// allocation riding the booking, and under it the item lines (product + qty).
//
// Contents are grouped one level up first — by the ORDER the PO belongs to (lead
// id + client name) — and within a group listed by PO number ascending, so a
// consolidated truck reads as "this client's stack, then that client's" rather
// than as one flat wall of POs. Every PO is filed under ITS OWN order, resolved
// per PO (see `lookupPoOrderContexts`) — a truck carries many orders' cargo, so the
// clicked row's lead says nothing about the other 36 POs aboard. `Download PDF`
// prints exactly this, in this order, as the sheet that rides with the truck.
//
// Deliberately minimal otherwise — carrier / route / status / dates are already
// on the board row that opened this, so none of that is repeated here.
//
// It is also the ONE surface on the board at the PO grain, which is why the
// "move to another transfer order" action lives here rather than in the row's
// More menu: moving is a per-PO decision, not a per-truck one. The button only
// appears on a TRANSFER truck (the server sends `transfer_stage_id`) and is
// disabled, with the reason, when the OMS says the goods are already moving.
// ============================================================================
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Empty, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DocPdfButtons from "../../components/doc_pdf_buttons";
import { fmtQty, fmtText } from "../../format";
import { LogisticsOrderRow } from "../../types";
import {
  getAllocationCargo,
  getLogisticsOrder,
  LogisticsCargoAllocation,
  LogisticsCargoItem,
  lookupPoOrderContexts,
  PoOrderContext,
  shipSeparately,
} from "../logistics.api";
import { AllocationGroup, groupAllocations } from "./allocation_groups";
import { downloadAllocationItemsPdf } from "./allocation_items_pdf";
import MoveTransferModal from "./move_transfer_modal";

interface Props {
  booking: LogisticsOrderRow | null; // the board row that was clicked (null → closed)
  onClose: () => void;
  onChanged?: () => void; // a move landed — the board behind this needs reloading
  // Drops the "Ship separately" button. Splitting a truck is an ops-desk call, so the Carrier
  // Dashboard opens this modal as a manifest only.
  hideShipSeparately?: boolean;
  // THE OTHER WAY IN: one allotment, for a board row that has no booking at all — the Missing Orders
  // and Awaiting Payment sections, where the whole point is that no truck was ever created. Clicking
  // such a row has to answer the same question ("what is in this order?"), so it opens this same
  // modal against the ALLOTMENT instead of a truck. Mutually exclusive with `booking`; the cargo
  // shape is identical, and the per-truck actions are absent because the server reports them false.
  allocation?: { id: number; label: string | null } | null;
}

const itemColumns: ColumnsType<LogisticsCargoItem> = [
  {
    title: "Product",
    key: "product",
    render: (_, it) => (
      <div>
        <div>{it.product?.product_name || "—"}</div>
        {it.product?.private_label_name && (
          <div style={{ fontSize: 12, color: "#531dab" }}>
            Private label: {it.product.private_label_name}
          </div>
        )}
        {it.product?.sku && (
          <div style={{ fontSize: 11, color: "#999" }}>SKU {it.product.sku}</div>
        )}
      </div>
    ),
  },
  {
    title: "Qty",
    key: "qty",
    width: 140,
    align: "right",
    render: (_, it) => (
      <span>
        {fmtQty(it.qty)}
        {it.unit ? <span style={{ color: "#999" }}> {it.unit}</span> : null}
      </span>
    ),
  },
];

const AllocationBlock: React.FC<{
  alloc: LogisticsCargoAllocation;
  onMove: (alloc: LogisticsCargoAllocation) => void;
  onShipSeparately: (alloc: LogisticsCargoAllocation) => void;
  hideShipSeparately?: boolean;
}> = ({ alloc, onMove, onShipSeparately, hideShipSeparately }) => (
  <div style={{ marginBottom: 20 }}>
    <Space size={8} wrap style={{ marginBottom: 8 }}>
      <Typography.Text strong>{alloc.po_number || "PO —"}</Typography.Text>
      {alloc.supplier_name && <Typography.Text type="secondary">{alloc.supplier_name}</Typography.Text>}
      {alloc.shipment_type && <Tag>{fmtText(alloc.shipment_type)}</Tag>}
      {alloc.transfer_order_number && <Tag color="geekblue">{alloc.transfer_order_number}</Tag>}
      {/* The PO and delivery challan for THIS PO. The modal is the only board surface at the PO
          grain, so it is the only place the documents riding with the driver can be pulled without
          opening each PO's order in turn — which is what loading a 37-PO truck used to mean. */}
      <DocPdfButtons docs={alloc.documents} />
      {alloc.transfer_stage_id && (
        // Disabled rather than hidden when it cannot move: the reason is the useful part — it tells
        // the operator the goods are already on their way, which is why they cannot re-plan them.
        <Tooltip title={alloc.move_blocked_reason || "Put this PO on a different transfer order"}>
          <Button size="small" disabled={!alloc.can_move} onClick={() => onMove(alloc)}>
            Move to another transfer
          </Button>
        </Tooltip>
      )}
      {alloc.shipment_stage_id && !hideShipSeparately && (
        // Customer shipment only — transfers split via "Move to another transfer" above.
        <Tooltip title={alloc.ship_separately_blocked_reason || "Take this PO off this truck and ship it on its own"}>
          <Button size="small" disabled={!alloc.can_ship_separately} onClick={() => onShipSeparately(alloc)}>
            Ship separately
          </Button>
        </Tooltip>
      )}
    </Space>
    <Table
      rowKey="id"
      size="small"
      columns={itemColumns}
      dataSource={alloc.items}
      pagination={false}
      locale={{ emptyText: "No item lines on this allocation." }}
    />
  </div>
);

// The order heading a stack of POs sits under. Kept visually heavier than the PO
// line inside it — the whole point of the grouping is that the two levels don't
// read as one list.
const GroupHeading: React.FC<{ group: AllocationGroup }> = ({ group }) => (
  <div
    style={{
      background: "#f5fbfc",
      borderLeft: "3px solid #24AAB7",
      padding: "6px 10px",
      marginBottom: 12,
    }}
  >
    <Space size={8} wrap>
      <Typography.Text strong>{group.label}</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {group.allocations.length} PO{group.allocations.length === 1 ? "" : "s"}
      </Typography.Text>
    </Space>
  </div>
);

const AllocationItemsModal: React.FC<Props> = ({
  booking,
  onClose,
  onChanged,
  hideShipSeparately,
  allocation,
}) => {
  const [allocations, setAllocations] = useState<LogisticsCargoAllocation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // PO number → the order it belongs to, resolved after the cargo lands.
  const [poContexts, setPoContexts] = useState<Record<string, PoOrderContext>>({});
  const [resolving, setResolving] = useState(false);
  const [movingAlloc, setMovingAlloc] = useState<LogisticsCargoAllocation | null>(null);

  // Re-runnable on purpose: a move takes a PO off this truck, and the modal has to show what is
  // actually left on it. It does NOT blank `allocations` — that would flash the list empty on a
  // refresh — so the reset of the previous booking's state lives in the effect below.
  const load = useCallback(() => {
    if (!booking && !allocation) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    // Two sources, one shape: a TRUCK and everything riding it, or ONE ALLOTMENT for a row that has
    // no truck to ask about. Everything below this point is identical either way.
    const fetch = booking
      ? getLogisticsOrder(booking.id)
      : getAllocationCargo(allocation!.id);
    fetch.then((res) => {
      if (cancelled) return;
      if (res) {
        setAllocations(res.allocations);
        // Armed here, not in the lookup effect: a frame that renders the cargo before it has been
        // attributed would show every PO under "Order not identified" and then reshuffle.
        setResolving(res.allocations.some((a) => a.po_number && !a.lead_id));
      } else setFailed(true);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [booking, allocation]);

  useEffect(() => {
    if (!booking && !allocation) return;
    setAllocations(null);
    // A different truck carries different POs — but the resolved contexts themselves are cached in
    // the api module, so dropping them here costs nothing on a re-open.
    setPoContexts({});
    setResolving(false);
    return load();
  }, [booking, allocation, load]);

  // Second pass: file each PO under its own order. Only POs that don't already carry a lead/client
  // are looked up, so this stops firing entirely once the cargo serializer returns them itself.
  useEffect(() => {
    const unresolved = (allocations ?? [])
      .filter((a) => a.po_number && !a.lead_id)
      .map((a) => a.po_number as string);
    if (!unresolved.length) return;
    let cancelled = false;
    lookupPoOrderContexts(unresolved).then((res) => {
      if (cancelled) return;
      setPoContexts(res);
      setResolving(false);
    });
    return () => {
      cancelled = true;
    };
  }, [allocations]);

  const confirmShipSeparately = (alloc: LogisticsCargoAllocation) => {
    const stageId = alloc.shipment_stage_id;
    if (!stageId) return;
    Modal.confirm({
      title: `Ship ${alloc.po_number || "this PO"} separately?`,
      content: "It comes off this truck and gets a truck of its own, with its own invoice and shipment.",
      okText: "Ship separately",
      onOk: async () => {
        if (await shipSeparately(stageId)) {
          load();
          onChanged?.();
        }
      },
    });
  };

  const groups = useMemo(
    () => (allocations ? groupAllocations(allocations, poContexts) : []),
    [allocations, poContexts]
  );

  const poCount = allocations?.length ?? 0;
  // The old title spelled out all 37 PO numbers, which pushed the contents off the screen and read
  // as if they all belonged to the row you clicked. The manifest itself is below, grouped by order.
  const title = booking
    ? `Contents · Booking #${booking.id}${
        poCount ? ` · ${poCount} PO${poCount === 1 ? "" : "s"}` : ""
      }${groups.length > 1 ? ` across ${groups.length} orders` : ""}`
    : allocation
      // Named by the ALLOTMENT, because there is no booking number to name it by.
      ? `Contents · ${allocation.label || "allotment"}`
      : "Contents";

  return (
    <Modal
      title={title}
      open={!!booking || !!allocation}
      onCancel={onClose}
      width={720}
      footer={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            // Held back until every PO is attributed — a sheet printed mid-resolution would file
            // cargo under the wrong order, and that sheet outlives the screen.
            disabled={!booking || !groups.length || resolving}
            loading={resolving}
            onClick={() => booking && downloadAllocationItemsPdf(booking, groups)}
          >
            Download PDF
          </Button>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Space>
      }
    >
      {loading || resolving ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
          {resolving && (
            <div style={{ marginTop: 12, color: "#8c8c8c", fontSize: 12 }}>
              Matching {allocations?.length ?? 0} POs to their orders…
            </div>
          )}
        </div>
      ) : failed ? (
        <Empty description="Could not load the contents of this logistics order." />
      ) : groups.length ? (
        groups.map((group) => (
          <div key={group.key} style={{ marginBottom: 8 }}>
            <GroupHeading group={group} />
            {group.allocations.map((a) => (
              <AllocationBlock
                key={a.id}
                alloc={a}
                onMove={setMovingAlloc}
                onShipSeparately={confirmShipSeparately}
                hideShipSeparately={hideShipSeparately}
              />
            ))}
          </div>
        ))
      ) : (
        <Empty description={booking ? "No allocations are riding this booking." : "This allotment has no item lines."} />
      )}
      <MoveTransferModal
        allocation={movingAlloc}
        onClose={() => setMovingAlloc(null)}
        onMoved={() => {
          // The PO has left this truck: refresh what is left on it, and the board behind us (the
          // moved PO now has a truck of its own, and this one may have been cancelled outright).
          load();
          onChanged?.();
        }}
      />
    </Modal>
  );
};

export default AllocationItemsModal;
